import axios from 'axios';
import { getAllEventTypes } from '../firebase/functions';
import {
  getCurrentToken,
  getCurrentTokenSafe,
  forceLogout,
  hasStoredSession,
} from '../utils/tokenManager';

// const BASE_URL = 'https://asia-south1-dashakswachhataka.cloudfunctions.net/';
const BASE_URL = 'https://kodv9vahdd.execute-api.ap-south-1.amazonaws.com/';
const LAMBDA_BASE_URL = 'https://kodv9vahdd.execute-api.ap-south-1.amazonaws.com/';

axios.defaults.baseURL = BASE_URL;

// Active campaign year, sent with every create call. The backend used to
// hardcode '2025' server-side; the V3/V4 endpoints take it from the request
// instead. Stored and compared as a STRING everywhere (event_year >= '2026'
// gates in the close forms, String column in report_events), so keep it one.
export const CAMPAIGN_YEAR = '2026';

// Create a separate axios instance for Lambda API
export const lambdaAxios = axios.create({
  baseURL: LAMBDA_BASE_URL,
});

// Public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
  '/login',
  '/refreshToken',
  '/sendOTP',
  '/verifyOTP',
  '/resetPasswordForUserName',
  '/getCounts',
  '/getCampaignDashboard2026',
  '/getSelfieDataById',
  '/getDashboardJanBhagidaariData',
  '/getPublicDashboardLatestView',
  '/getCtuReport2025',
  '/getEventTypeReport',
  '/getUserByUserId',
  // Public campaign listing behind the Reports page — the detail/create
  // endpoints on /hgt-hgs-2026 stay authenticated.
  '/hgt-hgs-2026/list',
  // Anonymous citizen selfie/pledge flow (/selfie -> /upload-selfie/:id and
  // /pledge -> /upload-pledge). None of these carry an authorizer server-side
  // (serverless-users.yml, serverless-media-signed-url.yml), so without them
  // here the interceptor rejects the submit for logged-out visitors with
  // "Authentication required but no token available".
  '/savePublicMediaData',
  '/updatePublicMedia',
  '/getSignedUrl',
  // Forced credential reset runs before the user has a token, by definition.
  '/credentialReset',
  // Same for the last step of forgot password / first-time password setup -
  // the user has just verified an OTP and has no token yet. The endpoint has
  // no authorizer server-side either, so the interceptor was only ever an
  // accidental gate.
  '/updatePasswordV2',
];

// Helper to check if endpoint is public - more robust matching
const isPublicEndpoint = (url) => {
  if (!url) return false;
  const cleanUrl = url.split('?')[0].replace(/^\/+/, ''); // Remove query params and leading slashes
  return PUBLIC_ENDPOINTS.some((endpoint) => {
    const cleanEndpoint = endpoint.replace(/^\/+/, ''); // Remove leading slashes from endpoint
    return (
      cleanUrl === cleanEndpoint ||
      cleanUrl.endsWith('/' + cleanEndpoint) ||
      cleanUrl.includes(cleanEndpoint)
    );
  });
};

// Axios interceptor for lambdaAxios (primary API instance)
lambdaAxios.interceptors.request.use(
  async (config) => {
    // Skip if it's a public endpoint
    if (isPublicEndpoint(config.url)) {
      return config;
    }

    // Skip if Authorization header already exists (from manual calls)
    if (config.headers?.Authorization) {
      return config;
    }

    // Add token automatically for protected endpoints
    const token = await getCurrentTokenSafe();
    if (token) {
      if (!config.headers) {
        config.headers = {};
      }
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    }

    // No token available for protected endpoint - reject request
    return Promise.reject(new Error('Authentication required but no token available'));
  },
  (error) => Promise.reject(error)
);

// --- 401 handling ----------------------------------------------------------
//
// A 401 from this API means the session is gone, not that the request was
// malformed. The only two things that produce one are an Authorization header
// the gateway cannot use (missing, or for a token it rejects outright) and
// requireAuth() failing inside a handler. Neither recovers on its own.
//
// Nothing used to read the status at all: the dead token stayed in
// localStorage, the route guard in FullLayout only checks that the storage
// keys *exist*, and isExpired() -- a check against the browser's own clock --
// is the only thing that ever triggered a refresh. So a user whose token the
// server rejects while the client still believes it is valid could never leave
// that state; every later action failed the same silent way until they cleared
// site data by hand. Logging them out is the exit.
//
// Endpoints the user talks to *before* they have a session are exempt. A wrong
// password on /login answers 401, and wiping storage plus reloading the page
// underneath the login form would destroy what they just typed along with the
// error explaining why. These are whole first path segments taken from the
// calls themselves, not guesses: the OTP pair the app actually uses is
// getOtpV1/*, with getOtp/* the older one still routed server-side.
const PRE_LOGIN_ENDPOINTS = [
  'login',
  'getOtp',
  'getOtpV1',
  'resetPasswordForUserName',
  'updatePasswordV2',
  'credentialReset',
];

// Path of a request URL, relative and stripped of its query string. Handles
// the absolute URLs a few callers pass as well as the usual bare endpoint name.
const requestPath = (url) => {
  if (!url) return '';
  let path = url;
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return '';
    }
  }
  return path.split('?')[0].replace(/^\/+/, '');
};

// Whole-segment match, deliberately NOT the loose `includes` test
// isPublicEndpoint uses above: as a substring, 'login' would also exempt any
// future endpoint with 'login' anywhere in its name.
const isPreLoginEndpoint = (url) => {
  const path = requestPath(url);
  return PRE_LOGIN_ENDPOINTS.some(
    (endpoint) => path === endpoint || path.startsWith(endpoint + '/')
  );
};

const handleUnauthorized = (error) => {
  if (error?.response?.status !== 401) return;
  if (isPreLoginEndpoint(error?.config?.url)) return;
  // Anonymous visitor on a public page: there is no session to end, and
  // bouncing them to /auth/login would be worse than the failed request.
  if (!hasStoredSession()) return;

  forceLogout();
};

const rejectAndLogout = (error) => {
  handleUnauthorized(error);
  // Rethrown either way. Callers keep whatever error handling they already
  // have; the logout is a side effect, not a replacement for it.
  return Promise.reject(error);
};

lambdaAxios.interceptors.response.use((response) => response, rejectAndLogout);

// The default instance needs it too. updateEvent, getAllStates and a long tail
// of other callers go through bare `axios` with a manually attached token, so
// an interceptor on lambdaAxios alone would miss them. Instances built with
// axios.create() do not inherit interceptors, so the throwaway clients used for
// direct-to-S3 presigned uploads stay untouched.
axios.interceptors.response.use((response) => response, rejectAndLogout);

const encodedURIcomponent = (url) => encodeURIComponent(url);

export const loginApi = async (username, password) => {
  try {
    const payload = {
      username: username,
      password: password,
    };

    return await lambdaAxios.post(`login`, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getUserDetails = async (idToken) => {
  try {
    return await lambdaAxios.get(`getUserProfile`, {
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const refreshToken = async (refreshTokenValue) => {
  try {
    const payload = {
      refreshToken: refreshTokenValue,
    };

    return await lambdaAxios.post('refreshToken', payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    console.error('refreshToken API call failed:', error);

    // Re-throw with original error for proper handling upstream
    throw error;
  }
};

export const resetPassword = async (payload) => {
  try {
    return await lambdaAxios.post(`updatePasswordV2`, payload);
  } catch (err) {
    console.log(err);
  }
};

export const getUserProfileByIdApi = async (user_id) => {
  try {
    const token = await getCurrentToken();
    return await lambdaAxios.get(`getUserProfile?user_id=${user_id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (err) {
    console.error('getUserProfileByIdApi failed:', err);
    throw err;
  }
};

export const getAllUsersByKeyApi = async (key, value, pageSize, lastEvaluatedKey) => {
  const token = await getCurrentToken();
  try {
    // Build query parameters
    let queryParams = `key=${encodeURIComponent(key)}&value=${encodeURIComponent(value)}`;

    if (pageSize) {
      queryParams += `&pageSize=${pageSize}`;
    }

    if (lastEvaluatedKey) {
      queryParams += `&lastEvaluatedKey=${encodeURIComponent(lastEvaluatedKey)}`;
    }

    return await lambdaAxios.get(`getAllUsersByKey?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getSelfieDataByID = async (id) => {
  try {
    return await axios.get(`getSelfieDataById?selfie_id=${id}`);
  } catch (e) {}
};

export const getCampaignCounts2025 = async () => {
  // const cacheUrl = `getCachedResult?url=${encodeURIComponent(BASE_URL + 'getCounts')}`;
  try {
    // Try to get cached data first
    // const cachedResponse = await axios.get(cacheUrl);
    // if (cachedResponse?.data) {
    //   return cachedResponse.data;
    // }
    // If no cached data, fallback to direct API call
    const response = await axios.get('getCounts');
    return response.data;
  } catch (err) {
    console.error('Error fetching campaign counts:', err);
    throw err;
  }
};

/**
 * Counts behind the SHS-2026 tab of /campaign-dashboard.
 *
 * Unlike the 2023-2025 tabs, which render frozen snapshots, this one is live:
 * the campaign is running. The endpoint is public, so no token is attached.
 */
export const getCampaignCounts2026 = async (eventYear = CAMPAIGN_YEAR) => {
  const response = await axios.get('getCampaignDashboard2026', {
    params: { event_year: eventYear },
  });
  return response.data;
};

export const getEventReportByUser = async (userId, eventThemeId, eventYear = CAMPAIGN_YEAR) => {
  try {
    return await lambdaAxios.get('getEventReportByUserV3', {
      params: {
        user_id: userId,
        event_theme_id: eventThemeId,
        // Without this the prefill would hand back a 2025 report when the
        // reporter opens a 2026 form.
        event_year: eventYear,
      },
    });
  } catch (error) {
    console.error('Error calling getEventReportByUser API:', error);
    throw error;
  }
};

export const getJanBhagidariData = async () => {
  try {
    return await axios.get(`getDashboardJanBhagidaariData`);
  } catch (err) {
    console.log(err);
  }
};
export const getCtuReport2025 = async (filters = {}) => {
  try {
    // Build query string from filters
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      params.append(key, filters[key]);
    });

    // Build the original URL
    const originalUrl = `${BASE_URL}getCtuReport2025?${params.toString()}`;

    // Use getCachedResultV1 for caching
    // const cachedUrl = `getCachedResultV1?url=${encodedURIcomponent(originalUrl)}`;

    const response = await axios.get(originalUrl);
    return response;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getEventTypeReport = async (filters = {}) => {
  try {
    // Build query string from filters
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        params.append(key, filters[key]);
      }
    });

    // Build the original URL
    const originalUrl = `${BASE_URL}getEventTypeReport?${params.toString()}`;

    // Use getCachedResultV1 for caching
    // const cachedUrl = `getCachedResultV1?url=${encodedURIcomponent(originalUrl)}`;

    const response = await axios.get(originalUrl);
    return response;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getCampaignCardData = async () => {
  try {
    return await axios.get(`getPublicDashboardLatestView`);
  } catch (err) {
    console.log(err);
  }
};

export const updatePermissions = async (payload) => {
  try {
    return await axios.post('addAndUpdatePermission', payload);
  } catch (err) {
    console.log(err);
  }
};

export const addRole = async (payload) => {
  try {
    return await axios.post('addRole', payload);
  } catch (err) {
    console.log(err);
  }
};

export const createUser = async (payload) => {
  try {
    const token = await getCurrentToken();
    return await axios.post('userCreationV2', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    console.error('createUser failed:', err);
    throw err;
  }
};

export const createCityReport = async (payload) => {
  const token = await getCurrentToken();
  try {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    return await axios.post('createCityReport', payload, { headers });
  } catch (err) {
    console.log(err);
  }
};

export const createCleanPublicSpacesEvent = async (payload) => {
  const token = await getCurrentToken();
  try {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    return await lambdaAxios.post('createCleanPublicSpacesV3', payload, { headers });
  } catch (err) {
    console.log(err);
  }
};

export const addEvent = async (payload) => {
  try {
    const token = await getCurrentToken(); // Auto-refreshes if needed
    return await lambdaAxios.post('createEventV4', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    console.error('addEvent failed:', err);
    throw err;
  }
};

export const addEventHGTHGS = async (payload) => {
  const token = await getCurrentToken();
  try {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    return await axios.post('createEvent-hgt-hgs', payload, { headers });
  } catch (err) {
    console.log(err);
  }
};

export const updateEvent = async (payload) => {
  const token = await getCurrentToken();
  try {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    return await axios.post('updateEventV1New', payload, { headers });
  } catch (err) {
    console.log(err);
  }
};

export const updateCPSEvent = async (payload) => {
  const token = await getCurrentToken();
  try {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    return await lambdaAxios.put('updateCleanPublicSpacesV2', payload, { headers });
  } catch (err) {
    console.log(err);
  }
};

export const closeCPSEvent = async (payload) => {
  const token = await getCurrentToken();
  try {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    return await lambdaAxios.post('closeCleanPublicSpacesV2', payload, { headers });
  } catch (err) {
    console.log(err);
  }
};

export const updateEventHGTHGS = async (payload) => {
  const token = await getCurrentToken();
  try {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    return await axios.post('updateEvent-V2', payload, { headers });
  } catch (err) {
    console.log(err);
  }
};

export const getPresignedUrl = async (filename, foldername) => {
  try {
    return await lambdaAxios.get(
      `getSignedUrl?folder=${foldername}&name=${filename}&action=write&storage=s3`
    );
  } catch (err) {
    console.log(err);
  }
};

// Action Plan (State / ULB). The PDF never passes through the API: the browser
// PUTs it straight to S3 with a presigned URL from getPresignedUrl, then this
// records the row. A 10 MB file base64-encodes to ~13.3 MB, over API Gateway's
// 10 MB request cap, so the direct upload is required rather than preferred.
export const ACTION_PLAN_FOLDER = 'action_plans';
export const ACTION_PLAN_MAX_BYTES = 10 * 1024 * 1024;

export const createActionPlan = async (payload) => {
  return lambdaAxios.post('action-plan', payload, {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const getActionPlans = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return lambdaAxios.get(`action-plan${query ? `?${query}` : ''}`);
};

// GESI-sensitive Training Schedule (State level). Same two-step flow as the
// Action Plan above: PUT to S3 through getPresignedUrl, then record the row.
// Keys are gesi_training_schedules_state/<year>/<state_code>/<name>.pdf.
export const TRAINING_SCHEDULE_FOLDER = 'gesi_training_schedules_state';
export const TRAINING_SCHEDULE_MAX_BYTES = 10 * 1024 * 1024;

export const createTrainingSchedule = async (payload) => {
  return lambdaAxios.post('gesi-training-schedule', payload, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// The caller's own upload for the year: an empty list, or one row.
export const getTrainingSchedules = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return lambdaAxios.get(`gesi-training-schedule${query ? `?${query}` : ''}`);
};

// GESI-sensitive Training, ULB level: participants, attendance register PDFs,
// certificate answer and session photos. Every file is PUT to S3 first through
// getPresignedUrl, then the form is submitted with the keys. Keys are
// gesi_training_schedules_ulb/<year>/<state_code>/<ulb_code>/{registers,photos}/...
export const TRAINING_SCHEDULE_ULB_FOLDER = 'gesi_training_schedules_ulb';

export const createTrainingScheduleUlb = async (payload) => {
  return lambdaAxios.post('gesi-training-schedule-ulb', payload, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// Edit the caller's submission. Same body as create: the full form, with the
// complete register and photo lists (kept files by their existing keys). Saved
// files left out of the lists are deleted from S3 by the server.
export const updateTrainingScheduleUlb = async (payload) => {
  return lambdaAxios.put('gesi-training-schedule-ulb', payload, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// The caller's own submission for the year, or training_submission: null.
export const getTrainingScheduleUlb = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return lambdaAxios.get(`gesi-training-schedule-ulb${query ? `?${query}` : ''}`);
};

// The State logo for the caller's own State (ULB logins), for the GESI
// certificate: { state_logo: { logo_file_key, ... } }, or state_logo: null
// when the State has not uploaded one yet.
export const getStateLogo = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return lambdaAxios.get(`gesi-state-logo${query ? `?${query}` : ''}`);
};

// Records a sanitation worker's name, age and gender when a ULB generates
// their GESI training certificate. Only the details are stored; the
// certificate image is drawn in the browser.
export const createTrainingWorker = async (payload) => {
  return lambdaAxios.post('gesi-training-worker', payload, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// My Profile page (ULB logins): save the signature. The image goes in the body
// as base64 - { file_name, data } - and the server writes it to S3 and its key
// onto the caller's own user_details row. PNG or JPEG, up to 1 MB.
export const ULB_SIGNATURE_MAX_BYTES = 1024 * 1024;

export const saveUlbSignature = async (payload) => {
  return lambdaAxios.put('ulb-signature', payload, {
    headers: { 'Content-Type': 'application/json' },
  });
};

// --- Home page twitter wall ---------------------------------------------
//
// The wall itself reads GET /getTweetIDs through firebase/functions.js, which
// is public and stays there. These two are the admin-only write half behind
// /add-tweet; both are authenticated, so they go through lambdaAxios and the
// token interceptor rather than the PUBLIC_ENDPOINTS list above.

export const createTweet = async (payload) => lambdaAxios.post('tweets', payload);

export const deleteTweet = async (id) => lambdaAxios.delete(`tweets/${id}`);

export const getPresignedUrlRead = async (filename, foldername) => {
  try {
    return await lambdaAxios.get(
      `getSignedUrl?folder=${foldername}&name=${filename}&action=read&storage=s3`
    );
  } catch (err) {
    console.log(err);
  }
};

/**
 * A short-lived link to a private S3 object, from its full key
 * ("<folder>/<rest>"). Throws when no link comes back.
 */
export const getSignedReadUrl = async (fileKey) => {
  const [folder, ...rest] = fileKey.split('/');
  const response = await getPresignedUrlRead(rest.join('/'), folder);
  if (response?.status !== 200 || !response?.data) {
    throw new Error('Could not get a link to the file');
  }
  return response.data;
};

export const createCityReportActivity = async (payload) => {
  try {
    return await axios.post('createCityReportActivity', payload);
  } catch (e) {
    console.log(e);
  }
};

export const createEventActivity = async (payload) => {
  const token = await getCurrentToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  try {
    return await lambdaAxios.post('createEventMediaV4', payload, { headers });
  } catch (e) {
    console.log(e);
  }
};
export const createEventActivityPubic = async (payload) => {
  try {
    return await lambdaAxios.post('/updatePublicMedia', payload);
  } catch (e) {
    console.log(e);
  }
};

export const createHGTHGSEventActivity = async (payload) => {
  const token = await getCurrentToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  try {
    return await axios.post('createMediaEvent-hgt-hgs', payload, { headers });
  } catch (e) {
    console.log(e);
  }
};

export const getSignedUrl = async (folder, name) => {
  try {
    return await axios.get(`getSignedUrl?folder=${folder}&name=${name}&action=read&storage=s3`);
  } catch (e) {
    console.log(e);
  }
};

export const eventClosure = async (payload) => {
  const token = await getCurrentToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  try {
    return await lambdaAxios.post('updateEventNew', payload, { headers });
  } catch (e) {
    console.log(e);
  }
};

export const eventClosureV2 = async (payload) => {
  const token = await getCurrentToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  try {
    return await lambdaAxios.post('createEventReportV3', payload, { headers });
  } catch (e) {
    console.log(e);
  }
};

// Removes a saved photo from the caller's own report (report-event edit mode).
// Unlike its neighbours this does not swallow the error: the caller counts
// failures and tells the user which removals did not go through.
export const deleteEventReportMedia = async (mediaId) => {
  const token = await getCurrentToken();
  return await lambdaAxios.delete(`deleteEventReportMedia/${encodeURIComponent(mediaId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
};

export const eventClosureMedia = async (payload) => {
  const token = await getCurrentToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  try {
    return await lambdaAxios.post('createEventMediaV4', payload, { headers });
  } catch (e) {
    console.log(e);
  }
};

// export const getEventDetailedReport = async (payload) => {
//   const token = await getCurrentToken();
//   const headers = {
//     'Content-Type': 'application/json',
//     Authorization: `Bearer ${token}`,
//   };
//   try {
//     return await axios.get('https://asia-south1-dashakswachhataka.cloudfunctions.net/exportCumulativeReportEventNameStateWise', payload, { headers });
//   } catch (e) {
//     console.log(e);
//   }
// };

export const getEventMediaByID = async (id) => {
  const token = await getCurrentToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  try {
    return await axios.get(`getEventMedia?event_id=${id}`, { headers });
  } catch (e) {
    console.log(e);
  }
};

export const getEventDetailedReport = async (payload) => {
  const token = await getCurrentToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  // Convert the payload to query string parameters
  const queryParams = new URLSearchParams(payload).toString();

  try {
    return await axios.get(
      `https://asia-south1-dashakswachhataka.cloudfunctions.net/exportCumulativeReportEventNameStateWise-v1c?${queryParams}`,
      { headers }
    );
  } catch (e) {
    console.log(e);
  }
};

export const getOtp = async (payload) => {
  try {
    return await axios.post('getOtpV1/sendOTP', payload);
  } catch (e) {
    return e;
  }
};

export const verifyOtp = async (payload) => {
  try {
    return await axios.post(`getOtpV1/verifyOTP`, payload);
  } catch (e) {
    return e.response;
  }
};

// ---------------------------------------------------------------------------
// Forced credential reset (DynamoDB + Cognito only, no Firebase).
// Users flagged with force_credential_reset confirm or replace the phone
// number we hold, verify it by OTP, then set a new password. login returns
// 403 CREDENTIAL_RESET_REQUIRED until this completes.
// ---------------------------------------------------------------------------

export const credentialResetStart = async (username) => {
  try {
    return await axios.post('credentialReset/start', { username });
  } catch (e) {
    return e.response;
  }
};

export const credentialResetSendOtp = async (userId, phone) => {
  try {
    return await axios.post('credentialReset/sendOtp', { user_id: userId, phone });
  } catch (e) {
    return e.response;
  }
};

export const credentialResetVerifyOtp = async (userId, otp) => {
  try {
    return await axios.post('credentialReset/verifyOtp', { user_id: userId, otp });
  } catch (e) {
    return e.response;
  }
};

export const credentialResetComplete = async (userId, resetToken, password) => {
  try {
    return await axios.post('credentialReset/complete', {
      user_id: userId,
      reset_token: resetToken,
      password,
    });
  } catch (e) {
    return e.response;
  }
};

export const userNameVerifyOtp = async (payload) => {
  try {
    return await axios.post(`resetPasswordForUserName/verifyOTP`, payload);
  } catch (e) {
    return e.response;
  }
};

export const userNamePasswordGetOtp = async (payload) => {
  try {
    return await axios.post(`resetPasswordForUserName/sendOTP`, payload);
  } catch (e) {
    return e.response;
  }
};

export const createPartner = async (data) => {
  return await axios.post(`partnerUserCreation`, data);
};

export const getDiwasCard = async () => {
  try {
    return await axios.get(
      `getCachedResult?url=${encodedURIcomponent(BASE_URL + `getLandingPageSBDData`)}`
    );
  } catch (err) {
    console.log(err);
  }
};

export const updatePartner = async (user_id) => {
  const token = await getCurrentToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  return await axios.post('partnerUserApprovalV2', { user_id }, { headers });
};

export const partnerJoinAndAdaptCTU = async (data) => {
  const token = await getCurrentToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  return await lambdaAxios.post('partnerUserJoinAndAdaptCtuV2', data, { headers });
};

export const approvePartnerJoinAndAdoptCTU = async (data) => {
  const token = await getCurrentToken();
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  return await axios.post('partnerEventAndCTUApprovalV2', data, { headers });
};

export const createSBM = async (data) => {
  return await axios.post('createSBMV1', data);
  // return await axios.post('createSBM', data);
};

export const createCitizenTestimonial = async (data) => {
  return await axios.post('createCitizenTestimonials', data);
};

// export const getEventCardDetails = async () => {
//   try {
//     const res = await getAllEventTypes();
//     const promises = res.map((item) =>
//       axios.post(`getMediaCount?event_type_id=${item?.event_type_id}`).then((response) => ({
//         event_type_id: item?.event_type_id,
//         event_name: item?.event_type_name,
//         theme: item?.theme,
//         event_data: response.data,
//       })),
//     );

//     const results = await Promise.all(promises);
//     return results; // return the collected results
//   } catch (e) {
//     console.log(e);
//   }
// };

export const getStateCount = async () => {
  try {
    return await axios.post(
      `getCachedResultV1?url=${encodedURIcomponent(
        BASE_URL +
          'getReportsLatest?resource=events&fields=["*","state_code"]&clickhouse_fields=["*","state_code"]&labels=["event_count"]&modifiers=["count"]&rank_by=event_count&categories=["state_code"]&sort=DESC&max_results=100&filters=[]'
      )}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getCTUCount = async () => {
  try {
    return await axios.post(
      `getCachedResultV1?url=${encodedURIcomponent(
        BASE_URL +
          'getReportsLatest?resource=events&fields=["*","state_code"]&clickhouse_fields=["*","state_code"]&labels=["event_count"]&modifiers=["count"]&rank_by=event_count&categories=["state_code"]&sort=DESC&max_results=100&filters=["event_heading_name=\'CTU Identification\'"]'
      )}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getTopCtuIdentifiedState = async () => {
  try {
    return await axios.post(
      `getReports?table_name=events&column_names=["event_id","state_code","state_name"]&aggregation_types=[ "count"]&group_by=["state_code","state_name"]&sort=DESC&limit=5&where_clauses=["event_heading_name='CTU Identification'"]&order=event_id`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getTopCtuIdentifiedArea = async () => {
  try {
    return await axios.post(
      `getReportsLatest?resource=events&fields=["*","area_type"]&clickhouse_fields=["*","area_type"]&labels=["event_count"]&modifiers=["count"]&rank_by=event_count&categories=["area_type"]&sort=DESC&max_results=4&filters=["event_heading_name=\'CTU Identification\'"]`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getTopGarbageCleaned = async () => {
  try {
    return await axios.post(
      `getCachedResultV1?url=${encodedURIcomponent(
        BASE_URL +
          'getReportsLatest?resource=event_media&fields=["state_code","approx_garbage_amount","state_name"]&clickhouse_fields=["state_code","approx_garbage_amount","state_name"]&labels=["","total_garbage_collected",""]&modifiers=["","sum",""]&rank_by=total_garbage_collected&categories=["state_code","state_name"]&sort=DESC&max_results=5&filters=["event_heading_name=\'CTU Identification\'"]'
      )}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getMediaImages = async (page, limit) => {
  try {
    // return await axios.get(
    //   `getCachedResult?url=${encodedURIcomponent(
    //     BASE_URL +
    //       `getLandingPageMedia?collection_name=moderated_event_media&page=${page}&limit=${limit}&moderated=true&moderation_accepted=true`
    //   )}`
    // );
    return await axios.get(
      BASE_URL +
        `getLandingPageMedia?collection_name=moderated_event_media&page=${page}&limit=${limit}&moderated=true&moderation_accepted=true`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getEventMediaImages = async (page, limit, event_id) => {
  try {
    return await axios.get(
      BASE_URL +
        `getLandingPageMedia?collection_name=moderated_event_media&page=${page}&limit=${limit}&moderated=true&moderation_accepted=true&event_id=${event_id}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getPublicMediaImages = async (page, limit, event_theme_id) => {
  try {
    return await axios.get(
      BASE_URL +
        `getLandingPageMedia?collection_name=moderated_event_media&page=${page}&limit=${limit}&event_theme_id=${event_theme_id}&moderated=true&moderation_accepted=true`
    );
  } catch (e) {
    console.log(e);
  }
};
// export const getSignedUrlLightBox = async (folder, name) => {
//   try {
//     return await axios.get(`getSignedUrl?folder=${folder}&name=${name}&action=read`);
//   } catch (e) {
//     console.log(e);
//   }
// };
export const getCtuDateRange = async () => {
  try {
    return await axios.post(
      `getCachedResultV1?url=${encodedURIcomponent(
        BASE_URL +
          'getReportsLatest?resource=events&fields=["*"]&clickhouse_fields=["*"]&labels=["count"]&modifiers=["count"]&categories=[]&max_results=100&sort=ASC&rank_by=date_range&filters=["event_heading_name=\'CTU Identification\'","created_at BETWEEN \'2024-09-05\' AND \'2024-10-05\'"]&date_ranges=[{"column":"created_at","start":"2024-09-05","end":"2024-09-07","label":"5 to 7"},{"column":"created_at","start":"2024-09-07","end":"2024-09-09","label":"7 to 9"},{"column":"created_at","start":"2024-09-09","end":"2024-09-11","label":"9 to 11"},{"column":"created_at","start":"2024-09-11","end":"2024-09-13","label":"11 to 13"},{"column":"created_at","start":"2024-09-13","end":"2024-09-15","label":"13 to 15"}]'
      )}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getReportsData = async (filters) => {
  console.log(filters);
  try {
    return await axios.post(
      `getCachedResultV1?url=${encodedURIcomponent(
        BASE_URL +
          `exportReportV1?resource=${filters.resource}&fields=${filters.fields}&clickhouse_fields=${filters.fields}&rank_by=${filters.rank_by}&filters=${filters.filters}`
      )}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getLandingPageData = async () => {
  try {
    return await axios.post(
      `getCachedResult?url=${encodedURIcomponent(BASE_URL + 'getLandingPageReports')}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getLandingPagePostData1 = async () => {
  try {
    return await axios.post(
      `getCachedResult?url=${encodedURIcomponent(BASE_URL + 'getDashboardCtuDataAndOtherV1')}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getLandingPagePostData2 = async () => {
  try {
    return await axios.post(
      `getCachedResult?url=${encodedURIcomponent(BASE_URL + 'getLandingPageCtuDataAndOtherV1')}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getLandingPagePostData3 = async () => {
  try {
    return await axios.post(
      `getCachedResult?url=${encodedURIcomponent(BASE_URL + 'getLandingPageDataLatestCampaignV1')}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getVisitorCount = async () => {
  try {
    return await axios.post(
      `getCachedResult?url=${encodedURIcomponent(BASE_URL + 'getVisitorCount')}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getReportsALL = async (type) => {
  try {
    return await axios.get(`exportEventAndUserData?type=${type}`);
  } catch (e) {
    console.log(e);
  }
};

export const downloadEventReport = async (event_type, user_id) => {
  try {
    // First API call to get the presigned URL
    const response = await axios.get(
      `downloadEventReport?event_type=${event_type}&user_id=${user_id}`
    );

    console.log(response.data, 'response from table');

    if (response.data.success && response.data.download_url) {
      // Create a new axios instance without interceptors for presigned URL download
      const axiosWithoutAuth = axios.create();

      // Download the file from the presigned URL
      const fileResponse = await axiosWithoutAuth.get(response.data.download_url, {
        responseType: 'blob', // Important: tell axios to expect a binary file
      });

      // Create blob URL and trigger download
      const blob = new Blob([fileResponse.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        response.data.filename || `${event_type.toUpperCase()}_Events_Report.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      throw new Error('Failed to get download URL from API');
    }

    return response;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const getCumilativeReports = async (query) => {
  try {
    const token = await getCurrentToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    return await axios.get(`exportReport?${query}`, {}, { headers });
  } catch (e) {
    console.log(e);
  }
};

export const getSbmAndCitizenReportData = async (query) => {
  try {
    const token = await getCurrentToken();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    return await axios.get(`getSbmAndCitizenReportData?${query}`, {}, { headers });
  } catch (e) {
    console.log(e);
  }
};

export const getReports = async () => {
  try {
    return await axios.post(
      `getCachedResultV1?url=${encodedURIcomponent(
        BASE_URL +
          `/getReportsLatest?resource=events&fields=["*","event_name_id","event_name"]&clickhouse_fields=["*","event_name_id","event_name"]&labels=["event_count"]&modifiers=["count"]&rank_by=event_count&categories=["event_name_id","event_name"]`
      )}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getEventThemeCount = async () => {
  try {
    return await axios.post(
      `getCachedResultV1?url=${encodedURIcomponent(
        BASE_URL +
          `/getReportsLatest?resource=events&fields=["*","event_theme_id"]&clickhouse_fields=["*","event_theme_id"]&labels=["event_count"]&modifiers=["count"]&rank_by=event_count&categories=["event_theme_id"]`
      )}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const getEventDetails = async (event_id, collection_name = null) => {
  try {
    const url = collection_name
      ? `getEventV2?collection_name=${collection_name}&event_id=${event_id}`
      : `getEventV2?event_id=${event_id}`;
    return await axios.get(url);
  } catch (e) {
    console.log(e);
  }
};

export const getPartnerReport = async (payload) => {
  try {
    return await axios.get('getPartnerCtuAdopJoinReportV2', {
      params: payload,
    });
  } catch (e) {
    console.log(e);
  }
};

export const publicEvents = async () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  try {
    return await axios.post('createMediaEventV1', payload, { headers });
  } catch (e) {
    console.log(e);
  }
};

export const getCampaignDashboardData = async (payload) => {
  const cacheUrl = `getCachedResult?url=${encodeURIComponent(
    BASE_URL + 'getPublicLandingPageDataV2?' + new URLSearchParams(payload).toString()
  )}`;

  try {
    // Try to get cached data directly
    const cachedResponse = await axios.get(cacheUrl);

    // Return the cached response if it exists
    if (cachedResponse?.data) {
      return cachedResponse;
    }

    // If no cached data, proceed with the original API call
    console.log('No cached data found, fetching from original endpoint.');
    return axios.get('getPublicLandingPageDataV2', {
      params: payload,
    });
  } catch (e) {
    console.error('Error fetching cached data:', e);
    return null; // or handle accordingly
  }
};

export const fetchBeforeAfterImages = async (page, limit) => {
  try {
    return await axios.get(
      BASE_URL + `getEventMediaBeforeAndAfterImages?page=${page}&limit=${limit}`
    );
  } catch (e) {
    console.log(e);
  }
};

export const totalParticipationCampaign = async (type, id) => {
  const cacheUrl = `getCachedResult?url=${encodeURIComponent(
    BASE_URL + `getLandingPageSDSD?type=${type}&event_heading_id=${id}`
  )}`;

  try {
    // Try to get cached data directly
    const cachedResponse = await axios.get(cacheUrl);
    // Return the cached response if it exists
    if (cachedResponse?.data) {
      return cachedResponse;
    }

    // Optionally, you could handle what happens if there's no cached data
    console.log('No cached data found.');
    return null; // or handle accordingly
  } catch (e) {
    console.log(e);
  }
};

export const totalCardParticipationCampaign = async () => {
  const cacheUrl = `getCachedResult?url=${encodeURIComponent(
    BASE_URL + `getLandingPageSDSD?type=total`
  )}`;

  try {
    // Try to get cached data directly
    const cachedResponse = await axios.get(cacheUrl);
    // Return the cached response if it exists
    if (cachedResponse?.data) {
      return cachedResponse;
    }

    // Optionally, you could handle what happens if there's no cached data
    console.log('No cached data found.');
    return null; // or handle accordingly
  } catch (e) {
    console.log(e);
  }
};

export const getDiwaliClosureReport = async (payload) => {
  try {
    const response = await axios.post('getLandingPageSDSD', payload);
    return response;
  } catch (e) {
    console.log(e);
  }
};

export const getHGTHGSReports = async (params) => {
  try {
    const { startDate, endDate, stateCode, districtCode } = params;
    let query = `${BASE_URL}getReports-hgt-hgs?start_date=${startDate}&end_date=${endDate}&event_type=DAILY_HGT_HGS`;

    if (stateCode) {
      query += `&state_code=${stateCode}`;
    }

    if (districtCode) {
      query += `&district_code=${districtCode}`;
    }

    const cacheUrl = `getCachedResultV1?url=${encodeURIComponent(query)}`;
    return await axios.get(cacheUrl);
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const savePublicMediaData = async (data) => {
  try {
    const response = await lambdaAxios.post('/savePublicMediaData', data);
    return response;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const getSafaiMitraShivirsData = async () => {
  try {
    // Build filters for SafaiMitra Suraksha Shivirs (event_theme_id = 3) with location_wise report type
    const filters = {
      report_type: 'location_wise',
      event_theme_id: 3, // SafaiMitra Suraksha Shivirs
    };

    // Call the Event Type Report API
    const response = await getEventTypeReport(filters);

    if (response?.data?.data && response.data.data.length > 0) {
      // Calculate totals for all SafaiMitra data across India
      let totals = {
        total_number_of_shivirs_conducted: 0,
        no_of_sanitation_workers_recognition: 0,
        no_of_preventive_health_checkups_organized: 0,
        health_participants: 0,
        no_ppe_kits_safety_gear_distributed: 0,
        total_beneficiaries: 0,
        total_health_ppe_beneficiaries: 0,
      };

      // Sum up all data from all states
      response.data.data.forEach((row) => {
        totals.total_number_of_shivirs_conducted +=
          parseInt(row.total_number_of_shivirs_conducted) || 0;
        totals.no_of_sanitation_workers_recognition +=
          parseInt(row.no_of_sanitation_workers_recognition) || 0;
        totals.no_of_preventive_health_checkups_organized +=
          parseInt(row.no_of_preventive_health_checkups_organized) || 0;
        totals.health_participants += parseInt(row.health_participants) || 0;
        totals.no_ppe_kits_safety_gear_distributed +=
          parseInt(row.no_ppe_kits_safety_gear_distributed) || 0;
        totals.total_beneficiaries += parseInt(row.total_beneficiaries) || 0;
      });

      // Calculate total health ppe beneficiaries as done in LocationWiseReport component
      totals.total_health_ppe_beneficiaries =
        totals.no_of_sanitation_workers_recognition +
        totals.health_participants +
        totals.no_ppe_kits_safety_gear_distributed +
        totals.total_beneficiaries;

      return { data: totals };
    } else {
      return { data: null };
    }
  } catch (err) {
    console.error('Error fetching SafaiMitra Shivirs data:', err);
    throw err;
  }
};

// Get fresh token using the production-grade token manager
// This function provides backward compatibility and uses the centralized token manager
const getApiToken = async () => {
  return await getCurrentToken();
};

export const getAllUsersApi = async (lastEvaluatedKey, pageSize) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    if (pageSize) {
      params.append('pageSize', pageSize);
    }

    // FIX: Don't double-encode lastEvaluatedKey
    // It's already URL-encoded from the API response
    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    const apiUrl = `getAllusers?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching users from API:', err);
    throw err;
  }
};

/**
 * Permanently delete a user. This is a HARD delete and cannot be undone: the
 * backend removes the `users` row, every `user_details` row, and the Cognito
 * account. Their past events are left alone, so those keep pointing at a
 * user_id that no longer resolves.
 *
 * MOHUA admin only -- the backend re-checks role_id against DynamoDB, so the
 * role gate on the Users page is a UI convenience, not the security boundary.
 */
export const deleteUserPermanently = async (userId) => {
  try {
    return await lambdaAxios.post('deleteUserV2', { user_id: userId });
  } catch (err) {
    console.error('deleteUserV2 failed:', err);
    throw err;
  }
};

/**
 * Permanently delete a CTU event. This is a HARD delete and cannot be undone: the
 * backend removes every photograph from S3, every `event_media` row and the `events`
 * row. There is no archive -- restoring a CTU means the ULB re-entering it.
 *
 * MOHUA admin only -- the backend re-checks role_id against DynamoDB, so the role
 * gate on the event list is a UI convenience, not the security boundary.
 *
 * Rethrows rather than swallowing, so the caller can surface the server's message.
 */
export const deleteEventPermanently = async (eventId) => {
  try {
    const res = await lambdaAxios.post('deleteEventV2', { event_id: eventId });
    return res.data;
  } catch (err) {
    console.error('deleteEventV2 failed:', err);
    throw err;
  }
};

export const getAllEventsToApproveApi = async (email, lastEvaluatedKey = null, pageSize = 25) => {
  try {
    const token = await getCurrentToken(); // Production-grade token with auto-refresh

    // Build query parameters using modern approach
    const params = new URLSearchParams();
    params.append('email', email);

    if (pageSize) params.append('pageSize', pageSize);
    if (lastEvaluatedKey) params.append('lastEvaluatedKey', lastEvaluatedKey);

    const apiUrl = `getAllEventsToApprove?${params.toString()}`;

    return await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (error) {
    console.error('getAllEventsToApproveApi failed:', error);
    throw error;
  }
};

export const getAllEventsToApproveForAdminApi = async (lastEvaluatedKey = null, pageSize = 25) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();

    // Add pagination parameters
    if (pageSize) {
      params.append('pageSize', pageSize);
    }

    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    // Use dedicated admin endpoint - no email parameter needed
    const apiUrl = `getAllEventsToApproveForAdmin?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching all events to approve from API (admin):', err);
    throw err;
  }
};

export const getJoinAdoptRequestByOrganisationPhoneApi = async (phone) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('phone', phone);

    const apiUrl = `getJoinAdoptRequestByOrganisationPhone?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching join/adopt requests by phone from API:', err);
    throw err;
  }
};

export const getJoinAdoptRequestByIdApi = async (userId, eventId) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('user_id', userId);
    params.append('event_id', eventId);

    const apiUrl = `getJoinAdoptRequestById?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching join/adopt request by ID from API:', err);
    throw err;
  }
};

export const getApprovedPartnerRequestByOrganisationPhoneApi = async (phone) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('phone', phone);
    params.append('approved', 'true');

    const apiUrl = `getApprovedPartnerRequestByOrganisationPhone?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching approved partner requests by phone from API:', err);
    throw err;
  }
};

export const getPartnerRequestByOrganisationPhoneApi = async (phone) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('phone', phone);
    params.append('approved', 'false');

    const apiUrl = `getApprovedPartnerRequestByOrganisationPhone?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (err) {
    console.error('Error fetching partner requests by phone from API:', err);
    throw err;
  }
};

export const getAllRequestNumberApi = async () => {
  try {
    const token = await getApiToken();

    const response = await lambdaAxios.get('getAllRequestNumber', {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching request number from API:', err);
    throw err;
  }
};

export const getRequestNumberForOwnedCTUApi = async (email) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('email', email);

    const apiUrl = `getRequestNumberForOwnedCTU?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching owned CTU request number from API:', err);
    throw err;
  }
};

export const getRequestNumberByLocationApi = async (locationType, locationCode) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('location_type', locationType);

    if (locationType === 'URBAN') {
      params.append('ulb_code', locationCode);
    } else if (locationType === 'RURAL') {
      params.append('district_code', locationCode);
    }

    const apiUrl = `getRequestNumberByLocation?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching request number by location from API:', err);
    throw err;
  }
};

export const getAllEventsByRoleIDApi = async (roleID, lastEvaluatedKey = null, pageSize = 25) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('role_id', roleID);
    if (pageSize) {
      params.append('pageSize', pageSize);
    }
    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    const apiUrl = `getAllEventsByRoleID?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching events by role ID from API:', err);
    throw err;
  }
};

export const getEventByIDApi = async (id) => {
  try {
    const token = await getApiToken();
    // Build query parameters
    const params = new URLSearchParams();
    params.append('id', id);

    const apiUrl = `getEventByID?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching event by ID from API:', err);
    throw err;
  }
};

export const getMediaByEventIDAndMediaTypeApi = async (
  eventId,
  mediaType,
  dailyEventDate = null
) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('event_id', eventId);
    params.append('event_media_type', mediaType);
    if (dailyEventDate) {
      params.append('daily_event_date', dailyEventDate);
    }

    const apiUrl = `getMediaByEventIDAndMediaType?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching media by event ID and type from API:', err);
    throw err;
  }
};

export const getMediaByTestimonialIDAndMediaTypeApi = async (testimonialId, mediaType) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('query_type', 'testimonial');
    params.append('testimonial_id', testimonialId);
    params.append('event_media_type', mediaType);

    const apiUrl = `getMediaByEventIDAndMediaType?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching media by testimonial ID and type from API:', err);
    throw err;
  }
};

export const getUserByUserNameApi = async (userName) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('userName', userName);

    const apiUrl = `getUserByUserName?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching user by username from API:', err);
    throw err;
  }
};

export const getUserByPhoneApi = async (phone) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('phone', phone);

    const apiUrl = `getUserByPhone?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching user by phone from API:', err);
    throw err;
  }
};

export const getUserByUserIdApi = async (userName) => {
  try {
    // PUBLIC ENDPOINT - No authentication required
    // This is used for pre-login user lookups

    // Build query parameters
    const params = new URLSearchParams();
    params.append('user_name', userName);

    const apiUrl = `getUserByUserId?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching user by user ID from API:', err);
    throw err;
  }
};

export const getAllPartnersDetailsApi = async (
  approved = false,
  pageSize = 50,
  lastEvaluatedKey = null
) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('approved', approved.toString());
    params.append('pageSize', pageSize.toString());

    // Only add lastEvaluatedKey if it exists and is not null/empty
    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    const apiUrl = `getAllPartnersDetails?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching partners details from API:', err);
    throw err;
  }
};

export const getPartnerEventDetailsApi = async (userId) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('user_id', userId);

    const apiUrl = `getPartnerEventDetails?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching partner event details from API:', err);
    throw err;
  }
};

export const getEventsToApproveByLocationApi = async (
  locationType,
  locationCode,
  lastEvaluatedKey = null,
  pageSize = 25
) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('location_type', locationType);

    if (locationType === 'URBAN') {
      params.append('ulb_code', locationCode);
    } else if (locationType === 'RURAL') {
      params.append('district_code', locationCode);
    }

    if (pageSize) {
      params.append('pageSize', pageSize);
    }

    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    const apiUrl = `getEventsToApproveByLocation?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching events to approve by location from API:', err);
    throw err;
  }
};

export const getAllEventsByQueryApi = async (
  lastEvaluatedKey = null,
  pageSize = 25,
  queryParams = {},
  conditionAllowed = false,
  removeCondition = false,
  page = 1
) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();

    // Add pagination parameters
    if (pageSize) {
      params.append('pageSize', pageSize);
    }

    // Use page-based pagination instead of cursor-based
    if (page) {
      params.append('page', page);
    }

    // Keep lastEvaluatedKey for backward compatibility (but prioritize page)
    if (lastEvaluatedKey && !page) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    // Add removeCondition parameter
    if (removeCondition) {
      params.append('removeCondition', 'true');
    }

    // Add conditionAllowed parameter
    if (conditionAllowed) {
      params.append('conditionAllowed', 'true');
    }

    // Add custom query parameters
    for (const [key, value] of Object.entries(queryParams)) {
      if (key && value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    }

    const apiUrl = `getAllEventsByQuery?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching events by query from API:', err);
    throw err;
  }
};

export const getAllEventsByKeyApi = async (
  lastEvaluatedKey = null,
  pageSize = 25,
  key,
  value,
  page = 1
) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();

    // Add required key and value parameters
    if (key) {
      params.append('key', key);
    }
    if (value !== undefined && value !== null && value !== '') {
      params.append('value', value);
    }

    // Add pagination parameters
    if (pageSize) {
      params.append('pageSize', pageSize);
    }

    // Use page-based pagination instead of cursor-based
    if (page) {
      params.append('page', page);
    }

    // Keep lastEvaluatedKey for backward compatibility (but prioritize page)
    if (lastEvaluatedKey && !page) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    const apiUrl = `getAllEventsByKey?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching events by key from API:', err);
    throw err;
  }
};

export const getAllCPSEventsByQueryApi = async (
  lastEvaluatedKey = null,
  pageSize = 25,
  queryParams = {}
) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();

    // Add pagination parameters
    if (pageSize) {
      params.append('pageSize', pageSize);
    }

    if (lastEvaluatedKey) {
      params.append('lastEvaluatedKey', lastEvaluatedKey);
    }

    // Add custom query parameters
    for (const [key, value] of Object.entries(queryParams)) {
      if (key && value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    }

    const apiUrl = `getAllCPSEventsByQuery?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching CPS events by query from API:', err);
    throw err;
  }
};

export const getEventsBySearchApi = async (userId, key = null, value = null, eventYear = null) => {
  try {
    const token = await getApiToken();

    // Build query parameters
    const params = new URLSearchParams();
    params.append('userId', userId);

    // Add optional key-value filter if provided
    if (key && value !== undefined && value !== null && value !== '') {
      params.append('key', key);
      params.append('value', value);
    }

    // Omitting event_year does NOT mean "every year" -- getEventsBySearch falls
    // back to DEFAULT_EVENT_YEAR ('2025'), so a search from the 2026 page came
    // back full of 2025 events. Always send the year the page is showing.
    if (eventYear !== undefined && eventYear !== null && eventYear !== '') {
      params.append('event_year', eventYear);
    }

    const apiUrl = `getEventsBySearch?${params.toString()}`;

    const response = await lambdaAxios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching events by search from API:', err);
    throw err;
  }
};

export const getAllEventsByQueryOtherApi = async (
  lastEvaluatedKey = null,
  pageSize = 10,
  queryParams = {},
  conditionAllowed = false
) => {
  try {
    const payload = {
      pageSize,
      queryParams,
      conditionAllowed,
    };

    if (lastEvaluatedKey) {
      payload.lastEvaluatedKey = lastEvaluatedKey;
    }

    const response = await lambdaAxios.post('getAllEventsByQueryOther', payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching events from getAllEventsByQueryOther API:', err);
    throw err;
  }
};

// Report Event Status list. Reads report_events from ClickHouse with page-based
// pagination and a real total, the same contract as getAllEventsByQueryApi.
export const getAllReportEventsByQueryApi = async (page = 1, pageSize = 25, queryParams = {}) => {
  const token = await getApiToken();
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('pageSize', pageSize);
  for (const [key, value] of Object.entries(queryParams)) {
    if (key && value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  }
  return lambdaAxios.get(`getAllReportEventsByQuery?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
};

export const getAllPartnersApi = async () => {
  try {
    const response = await lambdaAxios.get('getAllPartners', {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching partners from getAllPartners API:', err);
    throw err;
  }
};

export const getUserDetailsByUserIDApi = async (user_id) => {
  try {
    const response = await lambdaAxios.get(`getUserDetailsByUserID?user_id=${user_id}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response;
  } catch (err) {
    console.error('Error fetching user details by user ID from API:', err);
    throw err;
  }
};

// ---------------------------------------------------------------------------
// Har Ghar Tiranga - Har Ghar Swachhata 2026
//
// These use lambdaAxios so the Cognito token is attached by the interceptor —
// unlike the 2025 endpoints, which have no authorizer and pass headers by hand.
// Errors are rethrown rather than swallowed so the form can react to a 404
// (no report yet) or a 409 (one already exists).
// ---------------------------------------------------------------------------

export const getHgtHgs2026 = async () => {
  return await lambdaAxios.get('hgt-hgs-2026');
};

export const createHgtHgs2026 = async (payload) => {
  return await lambdaAxios.post('hgt-hgs-2026', payload);
};

export const updateHgtHgs2026 = async (payload) => {
  return await lambdaAxios.put('hgt-hgs-2026', payload);
};

export const createHgtHgs2026Media = async (payload) => {
  return await lambdaAxios.post('hgt-hgs-2026/media', payload);
};

export const deleteHgtHgs2026Media = async (id) => {
  return await lambdaAxios.delete(`hgt-hgs-2026/media/${id}`);
};

/**
 * Public listing for the Reports page — no token, no ULB scoping.
 * Filters are optional; pass only the ones the user has selected.
 */
export const getHgtHgs2026List = async ({ stateCode, districtCode, ulbCode } = {}) => {
  const params = {};
  if (stateCode) params.state_code = stateCode;
  if (districtCode) params.district_code = districtCode;
  if (ulbCode) params.ulb_code = ulbCode;

  return await lambdaAxios.get('hgt-hgs-2026/list', { params });
};
